import { Skeleton } from '@/components/ui/skeleton';
import type {
  ConversationDetail,
  ConversationMessage,
} from '@/features/conversations/api';
import { useMobileDashboardHeaderContent } from '@/features/mobile-dashboard-header';
import { useGdprConsentCompliance } from '@/lib/gdpr-consent-flag';
import { useResolvedRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';
import { ConversationMobileDetail as MainConversationMobileDetail } from './conversation-mobile-detail.legacy';

import {
  type ComposerDisabledReason,
  composerDisabledMessage,
} from './conversation-detail';
import { ConversationHandlerBadge } from './conversation-handler-badge';
import { ConversationMobileComposer } from './conversation-mobile-composer';
import {
  ConversationMobileMessageThread,
  useConversationMobileComposerLayout,
} from './conversation-mobile-message-thread';
import { ConversationPlatformAvatar } from './conversation-platform-avatar';
import { conversationLeadId } from './conversation-thread-events';
import { PhoneThreadActions } from './phone-thread-actions';
import { useConversationIdentity } from './use-conversation-identity';

interface ConversationMobileDetailProps {
  conversation: ConversationDetail;
  messages: ConversationMessage[];
  memberFirstNameById: Map<string, string>;
  memberFullNameById: Map<string, string>;
  memberImageById: Map<string, string | null>;
  isLoadingMessages?: boolean;
  onSend: (content: string) => Promise<unknown>;
  isSending?: boolean;
  composerDisabledReason?: ComposerDisabledReason;
  /** Merged inbox: which of the customer's channels the reply goes on. */
  replyChannel?: ReactNode;
  onBack?: () => void;
}

function ConversationMobileHeaderCenter({
  conversation,
  memberFirstNameById,
}: {
  conversation: ConversationDetail;
  memberFirstNameById: Map<string, string>;
}) {
  const routes = useResolvedRoutes();
  const { displayName, formName } = useConversationIdentity(conversation);
  const leadId = conversationLeadId(conversation);

  const identity = (
    <div className="flex min-w-0 flex-col items-center gap-1 py-0.5">
      <ConversationPlatformAvatar
        name={displayName}
        avatarUrl={conversation.externalUserAvatar}
        platform={conversation.platform}
        transport={conversation.transport}
        size="thread"
      />
      <div className="flex max-w-full flex-col items-center gap-0.5">
        <p className="max-w-full truncate text-[16px] font-bold leading-tight tracking-[-0.2px] text-[#0A0A0A]">
          {displayName}
        </p>
        {formName ? (
          <p className="max-w-full truncate text-xs text-muted-foreground">
            {formName}
          </p>
        ) : null}
        <ConversationHandlerBadge
          conversation={conversation}
          memberFirstNameById={memberFirstNameById}
          className="px-2 py-px text-[11px] font-medium leading-4"
        />
      </div>
    </div>
  );

  // Tapping who you are talking to opens their page under Customers.
  return leadId ? (
    <Link
      to={routes.customerDetail(leadId)}
      aria-label={`View ${displayName} in Customers`}
      className="flex min-w-0 justify-center rounded-xl px-3 transition active:bg-black/[0.05]"
    >
      {identity}
    </Link>
  ) : (
    identity
  );
}

function ConsentConversationMobileDetail({
  conversation,
  messages,
  memberFirstNameById,
  memberFullNameById,
  memberImageById,
  isLoadingMessages = false,
  onSend,
  isSending = false,
  composerDisabledReason = null,
  replyChannel = null,
  onBack,
}: ConversationMobileDetailProps) {
  // Keep the draft when policy refresh replaces the composer with a refusal.
  const [draft, setDraft] = useState('');
  const { composerLayoutVersion, onComposerLayoutChange } =
    useConversationMobileComposerLayout();

  const centerSlot = useMemo(
    () => (
      <ConversationMobileHeaderCenter
        conversation={conversation}
        memberFirstNameById={memberFirstNameById}
      />
    ),
    [conversation, memberFirstNameById]
  );

  useMobileDashboardHeaderContent({
    showBack: true,
    onBack,
    hideNotifications: true,
    alignItemsTop: true,
    centerSlot,
    hideTrailing: true,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <PhoneThreadActions
        key={conversation.id}
        conversationId={conversation.id}
        leadId={conversationLeadId(conversation)}
        compact
        className="shrink-0 border-b px-4 py-2.5"
      />
      {isLoadingMessages ? (
        <div className="flex flex-1 flex-col gap-2.5 px-4 py-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={index}
              className={cn(
                'h-11 w-[72%] rounded-[20px]',
                index % 2 === 1 && 'ml-auto w-[64%]'
              )}
            />
          ))}
        </div>
      ) : messages.length === 0 && conversation.platform !== 'phone' ? (
        <div className="flex flex-1 items-center justify-center px-4 py-8">
          <p className="text-center text-[15px] text-[#8E8E93]">
            No messages yet in this conversation.
          </p>
        </div>
      ) : (
        <ConversationMobileMessageThread
          conversation={conversation}
          messages={messages}
          memberFirstNameById={memberFirstNameById}
          memberFullNameById={memberFullNameById}
          memberImageById={memberImageById}
          composerLayoutVersion={composerLayoutVersion}
        />
      )}

      {composerDisabledReason ? (
        <div className="shrink-0 px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1.5">
          <p className="text-center text-[15px] text-[#8E8E93]">
            {composerDisabledMessage(composerDisabledReason)}
          </p>
          {composerDisabledReason === 'phone_call' ? (
            <div className="mt-2">
              <PhoneThreadActions
                key={conversation.id}
                conversationId={conversation.id}
                leadId={conversationLeadId(conversation)}
                messageOnly
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="shrink-0 border-t border-slate-100 bg-white/95 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-1">
            {replyChannel}
          </div>
          <ConversationMobileComposer
            draft={draft}
            onDraftChange={setDraft}
            onSend={onSend}
            disabled={!!composerDisabledReason}
            isSending={isSending}
            onLayoutChange={onComposerLayoutChange}
          />
        </div>
      )}
    </div>
  );
}

export function ConversationMobileDetail(
  props: Parameters<typeof ConsentConversationMobileDetail>[0]
) {
  const enabled = useGdprConsentCompliance();
  return enabled ? (
    <ConsentConversationMobileDetail {...props} />
  ) : (
    <MainConversationMobileDetail {...props} />
  );
}
